import "./style.scss";
import { useMemo, useState } from "react";
import CloseSvg from "@/src/svg/CustomUtils/CloseSvg";
import BtnDefault from "@/src/components/BtnDefault/BtnDefault";
import VerticalLine from "@/src/components/VerticalLine/VerticalLine";
import ProfileSvg from "@/src/svg/CustomUtils/ProfileSvg";
import { IApplyInfoComment } from "@/src/types/applicationOnline";
import { commentsRequests } from "@/src/api/commentsRequests";
import { sortCommentsNewestFirst } from "@/src/utils/officeAccountingMappers";
import { useFormatDateDisplay } from "@/src/hooks/useFormatDateDisplay";
import { stripCalcCommentMeta } from "@/src/utils/calculateCommentMeta";

type Props = {
  onClose: () => void;
  comments: IApplyInfoComment[];
  accountingId: number;
  title: string;
  onRefresh?: () => void | Promise<void>;
};

const TEXT_PLACEHOLDER = "—";

const CustomComments = ({ onClose, comments, accountingId, title, onRefresh }: Props) => {
  const formatDateDisplay = useFormatDateDisplay();
  const [noteValue, setNoteValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const displayedComments = useMemo(() => sortCommentsNewestFirst(comments), [comments]);

  const handleClear = async () => {
    if (isSubmitting || displayedComments.length === 0) return;

    const confirmed = window.confirm("Are you sure you want to delete all comments?");
    if (!confirmed) return;

    setIsSubmitting(true);
    try {
      const results = await Promise.all(displayedComments.map((comment) => commentsRequests.deleteAccountingComment(comment.id)));
      if (results.some((result) => result == null)) return;
      setNoteValue("");
      await onRefresh?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDone = async () => {
    const text = noteValue.trim();
    if (!text || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const result = await commentsRequests.createAccountingComment({
        text,
        accounting_id: accountingId,
      });
      if (result == null) return;
      setNoteValue("");
      await onRefresh?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="custom-comments">
      <div className="custom-comments-title">
        <div className="custom-comments-title-text">
          <p>{title}</p>
        </div>
        <div className="custom-comments-title-close" onClick={onClose}>
          <CloseSvg />
        </div>
      </div>
      <div className="custom-comments-list">
        {displayedComments.map((item) => (
          <div className="custom-comments-list-item" key={item.id}>
            <div className="custom-comments-list-item-header">
              <div className="custom-comments-list-item-info">
                <p>Commentator: {item.commentator || TEXT_PLACEHOLDER}</p>
                <p>Role: {item.commentator_role || TEXT_PLACEHOLDER}</p>
                <p>Date: {item.created_at ? formatDateDisplay(item.created_at) : TEXT_PLACEHOLDER}</p>
              </div>
              <div className="custom-comments-list-item-logo">
                {item.commentator_photo ? <img src={item.commentator_photo} alt="" /> : <ProfileSvg />}
              </div>
            </div>
            <div className="custom-comments-list-item-body">
              <p>{stripCalcCommentMeta(item.text || "") || TEXT_PLACEHOLDER}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="custom-comments-note">
        <textarea placeholder="Note..." value={noteValue} onChange={(e) => setNoteValue(e.target.value)} disabled={isSubmitting} />
      </div>
      <div className="custom-comments-buttons">
        <BtnDefault onClick={handleClear} disabled={isSubmitting || displayedComments.length === 0}>
          <p>Clear</p>
        </BtnDefault>
        <BtnDefault onClick={handleDone} disabled={isSubmitting || !noteValue.trim()}>
          <p>Done</p>
        </BtnDefault>
      </div>
    </div>
  );
};

export default CustomComments;
